import classnames from 'classnames'
import { OfferStatus, TimeZone } from '../../../api-client/enums'
import { Button } from '../../../components/button'
import PurchaseTag from '../../../components/svg/purchase-tag'
import { useAuth } from '../../../contexts/auth'
import { VIEWS } from '../../auth/modals'
import useVehiclePageData from '../use-vehicle-page-data'
import { differenceInDays, differenceInSeconds, now, parseISO } from '../../../utils/date-service'
import { useMemo } from 'react'
import RemainingTime from '../../../components/remaining-time'

const Timer = ({ expirationDate }) => {
  return (
    <div className='root'>
      <RemainingTime timeZone={TimeZone.None} endDate={expirationDate} />
      <div className='label'>Remaining Time</div>
      <style jsx>{`
        .root {
          display: flex;
          flex-direction: column;
          color: var(--primary-color);
          font-size: 1.8rem;
          font-weight: 700;

          .label {
            color: var(--secondary-text-color);
            font-size: 1.2rem;
            font-weight: normal;
          }
        }

        @media (max-width: 1024px) {
          .root {
          }
        }
      `}</style>
    </div>
  )
}

export default function BuyNowInfo({ onMakeOfferClick, onBuyNowClick, status, directOfferExists }) {
  const { buyNowListingInfo } = useVehiclePageData()
  const [{ isAuthenticated }, { setAuthModal }] = useAuth()

  const expirationDateISO = buyNowListingInfo?.expirationDate ?? ''

  const expirationDate = parseISO(expirationDateISO, { withTime: true })
  const isExpired = differenceInSeconds(expirationDate, now()) <= 0

  const expiryMessage = useMemo(() => {
    if (!expirationDate) return 'Starting soon'
    if (isExpired) return 'Expired'

    let inDays = differenceInDays(expirationDate, now())
    if (inDays > 0) return 'Expires in ' + inDays + ' day' + (inDays > 1 ? 's' : '')

    return <Timer expirationDate={expirationDate} />
  }, [expirationDate, isExpired])

  if (!buyNowListingInfo) return null

  return (
    <div
      className={classnames('buyNowInfo flex justifyBetween', {
        justifyCenter: !buyNowListingInfo.hasDirectOffers || isExpired,
      })}
    >
      <div className='buttonWrapper'>
        {!isExpired ? (
          <Button
            className='flex alignCenter justifyCenter'
            onClick={
              isAuthenticated
                ? onBuyNowClick
                : () => setAuthModal({ open: true, view: VIEWS.Login })
            }
          >
            <PurchaseTag />
            <span>Buy it Now</span>
          </Button>
        ) : (
          ''
        )}
        <div className='info'>{expiryMessage}</div>
      </div>

      {buyNowListingInfo.hasDirectOffers && !isExpired && (
        <div className='buttonWrapper'>
          <Button
            onClick={
              isAuthenticated
                ? onMakeOfferClick
                : () => setAuthModal({ open: true, view: VIEWS.Login })
            }
            disabled={status === OfferStatus.Cancelled}
          >
            {status === OfferStatus.Cancelled
              ? 'Offer'
              : directOfferExists
              ? 'View Offer'
              : 'Make an Offer'}
          </Button>
          <div className='info'>
            Min.{' '}
            {buyNowListingInfo.minimumOfferAmount.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </div>
        </div>
      )}

      <style jsx>{`
        .buyNowInfo {
          margin: 1.5rem 0 1.6rem;
          padding: 1.5rem;
          border-radius: 1rem;
          background: #1a1e21;

          :global(button) {
            width: 16.4rem;
            padding: 0;
            border-radius: 3.9rem;

            span {
              margin: 0 0 0 0.9rem;
            }
          }

          &.justifyCenter {
            :global(button) {
              width: 27.8rem;
            }
          }
        }

        .buttonWrapper {
          text-align: center;
        }

        .blueBtn {
          background: var(--primary-color);
        }

        .info {
          margin: 0.4rem 0 0;
          color: var(--secondary-text-color);
          font-size: 1.8rem;
        }

        @media (min-width: 1025px) and (max-width: 1364px) {
          .buyNowInfo {
            :global(button) {
              min-width: unset;
              width: 12.4rem;
              height: 3.8rem;
              font-size: 1.4rem;
            }
          }

          .info {
            font-size: 1.4rem;
          }
        }

        @media (max-width: 1024px) {
          .buyNowInfo {
            width: 100%;
            margin: 0;
            padding: 0;
            background: #fff;

            :global(button) {
              margin: 0 0.35rem;
            }

            :global(svg) {
              width: 14.54px;
              height: 14.54px;
            }
          }

          .info {
            font-size: 1.4rem;
          }
        }
      `}</style>
    </div>
  )
}
